import { FiPlus, FiChevronDown } from "react-icons/fi";
import { Badge } from "@/components/common/Badge";
import { Button } from "@/components/common/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/common/DropdownMenu";
import { cn } from "@/lib/utils";
import { useAppI18n } from "@/hooks/useAppI18n";
import { getCreatorSegments, getReviewerSegments, getSecondaryActions, shouldShowContentFilters} from "@/services/workspace";
import type { WorkspaceView, UserRole, ViewMode, ContentTypeFilter } from "@/types/workspaceTypes";
import WorkspaceSearch from "./WorkspaceSearch";
import WorkspaceContentFilters from "./WorkspaceContentFilters";

interface WorkspaceToolbarProps {
  activeView: WorkspaceView;
  secondaryView: WorkspaceView | null;
  onViewChange: (view: WorkspaceView) => void;
  onSecondaryActionChange: (action: WorkspaceView) => void;
  userRole: UserRole;
  onRoleChange: (role: UserRole) => void;
  hasCreatorRole?: boolean;
  hasReviewerRole?: boolean;
  /** True when user has BOOK_CREATOR but not CONTENT_CREATOR */
  isBookCreatorOnly?: boolean;
  /** True when user has BOOK_REVIEWER but not CONTENT_REVIEWER */
  isBookReviewerOnly?: boolean;
  counts: { drafts: number; review: number; published: number; all: number; pendingReview?: number };
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  typeFilter: ContentTypeFilter;
  onTypeFilterChange: (filter: ContentTypeFilter) => void;
  transcriptFilter: boolean;
  onTranscriptFilterChange: (value: boolean) => void;
  contentCount?: number;
  totalCount?: number;
  onCreateClick: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const WorkspaceToolbar = ({
  activeView,
  secondaryView,
  onViewChange,
  onSecondaryActionChange,
  userRole,
  onRoleChange,
  hasCreatorRole = false,
  hasReviewerRole = false,
  isBookCreatorOnly = false,
  isBookReviewerOnly = false,
  counts,
  viewMode,
  onViewModeChange,
  typeFilter,
  onTypeFilterChange,
  transcriptFilter,
  onTranscriptFilterChange,
  contentCount,
  totalCount,
  onCreateClick,
  searchQuery,
  onSearchChange,
}: WorkspaceToolbarProps) => {
  const { t } = useAppI18n();

  const segments =
    userRole === 'creator' ? getCreatorSegments(counts) : getReviewerSegments(counts);
  const showContentFilters = shouldShowContentFilters(activeView);
  const secondaryActions = getSecondaryActions(userRole, isBookCreatorOnly);
  const showRoleSwitcher = hasCreatorRole || hasReviewerRole;
  const selectedSecondaryAction = secondaryView
    ? secondaryActions.find(a => a.id === secondaryView)
    : undefined;

  return (
    <div className="space-y-4 mb-6">
      {/* Top Bar: Role + Create + Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Role Switcher - Minimal */}
          {showRoleSwitcher && (
            <div className="flex items-center gap-1 text-sm font-rubik">
              {hasCreatorRole && (
                <button
                  onClick={() => onRoleChange('creator')}
                  className={cn(
                    "px-3 py-1.5 rounded-lg transition-all",
                    userRole === 'creator'
                      ? "text-sunbird-theme-accent font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t('workspace.roleCreator')}
                </button>
              )}
              {hasCreatorRole && hasReviewerRole && (
                <span className="text-muted-foreground">|</span>
              )}
              {hasReviewerRole && (
                <button
                  onClick={() => onRoleChange('reviewer')}
                  className={cn(
                    "px-3 py-1.5 rounded-lg transition-all",
                    userRole === 'reviewer'
                      ? "text-sunbird-theme-accent font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t('workspace.roleReviewer')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 sm:ml-auto">
          {userRole === 'creator' && (
            <Button onClick={onCreateClick} size="lg" className="bg-sunbird-theme-accent hover:bg-sunbird-theme-accent/90 text-white font-rubik rounded-2xl shadow-lg px-6">
              <FiPlus className="w-5 h-5 mr-2" />
              {t('createNew')}
            </Button>
          )}
        </div>
      </div>

      {/* Segmented Control Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-border p-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Main Segments */}
          <div className="flex bg-gray-100 rounded-xl p-1 w-full md:w-auto md:max-w-xl min-w-0 overflow-x-auto scrollbar-hide">
            {segments.map((segment) => (
              <button
                key={segment.id}
                onClick={() => onViewChange(segment.id)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium font-rubik transition-all",
                  activeView === segment.id
                    ? "bg-white text-sunbird-theme-accent shadow-sm"
                    : "text-foreground hover:text-sunbird-theme-accent"
                )}
              >
                <span>{t(segment.label)}</span>
                {segment.count !== undefined && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      "min-w-[20px] justify-center text-xs",
                      activeView === segment.id
                        ? "bg-sunbird-theme-accent-muted/20 text-sunbird-theme-accent border-transparent"
                        : "bg-gray-200/70 text-foreground border-transparent"
                    )}
                  >
                    {segment.count}
                  </Badge>
                )}
              </button>
            ))}
          </div>

          {/* Search + Secondary Actions + Filters */}
          <div className="flex items-center gap-3 w-full md:w-auto min-w-0 flex-wrap md:flex-nowrap justify-between md:justify-end">
            <WorkspaceSearch query={searchQuery} onChange={onSearchChange} />

            {/* Secondary Dropdown */}
            {secondaryActions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="font-rubik rounded-xl flex-shrink-0">
                    {selectedSecondaryAction
                      ? t(selectedSecondaryAction.label)
                      : t('workspace.more')}
                    <FiChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  {secondaryActions.map((action) => (
                    <DropdownMenuItem
                      key={action.id}
                      onClick={() => onSecondaryActionChange(action.id)}
                      className="font-rubik"
                    >
                      {t(action.label)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Filters + View Mode (show when content is visible) */}
            {showContentFilters && (
              <WorkspaceContentFilters
                isBookCreatorOnly={isBookCreatorOnly}
                isBookReviewerOnly={isBookReviewerOnly}
                typeFilter={typeFilter}
                onTypeFilterChange={onTypeFilterChange}
                transcriptFilter={transcriptFilter}
                onTranscriptFilterChange={onTranscriptFilterChange}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      {userRole === 'creator' && showContentFilters && (
        <div className="flex items-center gap-6 px-2">
          {/* Section titles for secondary views */}
          {secondaryView === 'uploads' && (
            <span className="text-sm font-semibold font-rubik text-foreground">{t('workspace.stats.allUploads')}</span>
          )}
          {secondaryView === 'collaborations' && (
            <span className="text-sm font-semibold font-rubik text-foreground">{t('workspace.stats.myCollaborations')}</span>
          )}
          {contentCount !== undefined && (
            <span className="text-sm text-muted-foreground font-rubik ml-auto">
              {totalCount !== undefined && totalCount > contentCount!
                ? t('workspace.showingItemsOf', { count: contentCount, total: totalCount })
                : t('workspace.showingItems', { count: contentCount })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspaceToolbar;
